import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid3X3, UtensilsCrossed, QrCode, Eye, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import OwnerLayout from '../../components/OwnerLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

export default function OwnerDashboard() {
  const [restaurant, setRestaurant] = useState(null);
  const [stats, setStats] = useState({ items: 0, categories: 0 });
  const [loading, setLoading] = useState(true);
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [rRes, cRes, iRes] = await Promise.all([
          api.get('/api/owner/restaurant'),
          api.get('/api/owner/categories'),
          api.get('/api/owner/items'),
        ]);
        setRestaurant(rRes.data);
        setStats({ categories: cRes.data.length, items: iRes.data.length });
      } catch { toast.error('Failed to load data'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const menuUrl = `${window.location.origin}/menu/${restaurant?.id}`;

  return (
    <OwnerLayout title="Dashboard">
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
        <div className="fade-in">
          {/* Restaurant Info */}
          <div className="card" style={styles.heroCard}>
            <div style={styles.heroTop}>
              <div style={styles.logoBox}>
                {restaurant?.logo ? (
                  <img src={restaurant.logo} alt="" style={styles.logoImg} />
                ) : (
                  <span style={{ fontSize: 36 }}>🍽️</span>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <h2 style={styles.restaurantName}>{restaurant?.name}</h2>
                <p style={styles.restaurantSub}>📱 {restaurant?.whatsapp}</p>
                {restaurant?.address && <p style={styles.restaurantSub}>📍 {restaurant?.address}</p>}
              </div>
              <button onClick={() => { logout(); navigate('/login'); }} style={styles.logoutBtn}>
                <LogOut size={18} color="#7F8C8D" />
              </button>
            </div>
            <div style={{ marginTop: 12 }}>
              <span className={`badge ${restaurant?.is_active ? 'badge-success' : 'badge-danger'}`}>
                {restaurant?.is_active ? '● Active' : '● Inactive'}
              </span>
            </div>
          </div>

          {/* Stats */}
          <div style={styles.statsRow}>
            <div style={styles.statCard}>
              <Grid3X3 size={22} color="#FF6B35" />
              <span style={styles.statNum}>{stats.categories}</span>
              <span style={styles.statLabel}>Categories</span>
            </div>
            <div style={styles.statCard}>
              <UtensilsCrossed size={22} color="#FF6B35" />
              <span style={styles.statNum}>{stats.items}</span>
              <span style={styles.statLabel}>Menu Items</span>
            </div>
          </div>

          {/* Quick Actions */}
          <h3 style={styles.sectionTitle}>Quick Actions</h3>
          <div style={styles.actionsGrid}>
            <button className="card" style={styles.actionBtn} onClick={() => navigate('/owner/categories')}>
              <Grid3X3 size={28} color="#FF6B35" />
              <span style={styles.actionLabel}>Categories</span>
            </button>
            <button className="card" style={styles.actionBtn} onClick={() => navigate('/owner/menu')}>
              <UtensilsCrossed size={28} color="#FF6B35" />
              <span style={styles.actionLabel}>Menu Items</span>
            </button>
            <button className="card" style={styles.actionBtn} onClick={() => navigate('/owner/qrcode')}>
              <QrCode size={28} color="#FF6B35" />
              <span style={styles.actionLabel}>My QR Code</span>
            </button>
            <button className="card" style={styles.actionBtn} onClick={() => window.open(menuUrl, '_blank')}>
              <Eye size={28} color="#FF6B35" />
              <span style={styles.actionLabel}>Preview Menu</span>
            </button>
          </div>

          {/* Menu Link */}
          <div className="card" style={styles.linkCard}>
            <p style={styles.linkLabel}>Your Menu Link</p>
            <p style={styles.linkUrl}>{menuUrl}</p>
            <button
              className="btn btn-secondary btn-sm"
              style={{ marginTop: 8 }}
              onClick={() => { navigator.clipboard.writeText(menuUrl); toast.success('Link copied!'); }}
            >
              Copy Link
            </button>
          </div>
        </div>
      )}
    </OwnerLayout>
  );
}

const styles = {
  heroCard: { marginBottom: 16 },
  heroTop: { display: 'flex', gap: 12, alignItems: 'flex-start' },
  logoBox: { width: 60, height: 60, background: '#fff0eb', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  logoImg: { width: 60, height: 60, borderRadius: 12, objectFit: 'cover' },
  restaurantName: { fontSize: '17px', fontWeight: '700', color: '#2C3E50', marginBottom: 2 },
  restaurantSub: { fontSize: '12px', color: '#7F8C8D', marginBottom: 2 },
  logoutBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 4 },
  statsRow: { display: 'flex', gap: 12, marginBottom: 20 },
  statCard: { flex: 1, background: 'white', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
  statNum: { fontSize: '28px', fontWeight: '700', color: '#2C3E50' },
  statLabel: { fontSize: '12px', color: '#7F8C8D' },
  sectionTitle: { fontSize: '15px', fontWeight: '600', color: '#2C3E50', marginBottom: 12 },
  actionsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 },
  actionBtn: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '20px 12px', cursor: 'pointer', border: 'none', background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', transition: 'transform 0.2s' },
  actionLabel: { fontSize: '13px', fontWeight: '500', color: '#2C3E50' },
  linkCard: { background: '#fff0eb' },
  linkLabel: { fontSize: '12px', fontWeight: '600', color: '#FF6B35', marginBottom: 4 },
  linkUrl: { fontSize: '12px', color: '#7F8C8D', wordBreak: 'break-all' },
};
